import {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useLiveTour, ASSESSMENT_TOUR_ROUTES } from "../../context/LiveTourContext";
import { ELEMENT_TYPES } from "../../data/liveTourSteps";
import {
  resolveTooltipPosition,
  resolveCenteredPosition,
  resolveStepSize,
  getMaxTooltipHeight,
  ensureTooltipInViewport,
  COMPACT_MAX_W,
  STANDARD_MAX_W,
} from "./liveTourPosition";
import { findTourTarget, clipRectToViewport } from "./liveTourDom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MousePointer2,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  Layers,
  Pin,
} from "lucide-react";

export default function LiveTourOverlay() {
  const location = useLocation();
  const assessmentPage = ASSESSMENT_TOUR_ROUTES.includes(location.pathname);

  const {
    active,
    currentStep,
    stepIndex,
    totalSteps,
    targetReady,
    targetMissing,
    waitingForTarget,
    tourLabel,
    nextStep,
    prevStep,
    endTour,
  } = useLiveTour();

  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({
    top: 0,
    left: 0,
    docked: false,
    placed: false,
  });
  const [maxTooltipH, setMaxTooltipH] = useState(() => getMaxTooltipHeight());
  const [bodyScrollable, setBodyScrollable] = useState(false);
  const [atBodyBottom, setAtBodyBottom] = useState(true);
  const tooltipRef = useRef(null);
  const bodyRef = useRef(null);

  const stepSize = currentStep ? resolveStepSize(currentStep) : "standard";
  const isCompact = stepSize === "compact";
  const maxW = isCompact ? COMPACT_MAX_W : STANDARD_MAX_W;

  const stepKey = currentStep?.id || currentStep?.targetId || stepIndex;
  const tooltipVisible = Boolean(tooltipPos.placed && tooltipPos.stepKey === stepKey);

  const updateRect = useCallback(() => {
    if (!active || !currentStep) {
      setRect(null);
      return;
    }
    const el = findTourTarget(currentStep.targetId);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect(
      clipRectToViewport({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      }),
    );
  }, [active, currentStep]);

  useEffect(() => {
    setBodyScrollable(false);
    setAtBodyBottom(true);
  }, [stepKey]);

  useLayoutEffect(() => {
    updateRect();
  }, [updateRect, targetReady, stepIndex, active, currentStep]);

  useEffect(() => {
    if (!active || !currentStep) {
      document
        .querySelectorAll(".live-tour-target-active")
        .forEach((el) => el.classList.remove("live-tour-target-active"));
      return undefined;
    }
    const el = findTourTarget(currentStep.targetId);
    document.querySelectorAll(".live-tour-target-active").forEach((node) => {
      if (node !== el) node.classList.remove("live-tour-target-active");
    });
    if (targetReady && el) el.classList.add("live-tour-target-active");
    return () => el?.classList.remove("live-tour-target-active");
  }, [active, currentStep, targetReady, stepIndex]);

  useEffect(() => {
    if (!active) return undefined;
    const onScrollOrResize = () => {
      updateRect();
      setMaxTooltipH(getMaxTooltipHeight());
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    const interval = window.setInterval(updateRect, 300);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.clearInterval(interval);
    };
  }, [active, updateRect]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        endTour();
        return;
      }
      if (e.repeat) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        nextStep();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        prevStep();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, endTour, nextStep, prevStep]);

  const showSpotlight = !!(rect && targetReady);

  // Recompute the tooltip's screen position. Runs whenever the target rect,
  // readiness, step, or the tooltip's own measured size changes — a
  // ResizeObserver (below) keeps this in sync even when content height
  // settles a tick after the DOM update, which is what previously caused
  // the footer buttons to land outside the viewport.
  const recomputePosition = useCallback(() => {
    if (!tooltipRef.current || !currentStep) return;
    const { width, height } = tooltipRef.current.getBoundingClientRect();
    if (!width || !height) return;
    const pos =
      rect && targetReady
        ? resolveTooltipPosition(
            rect,
            width,
            height,
            currentStep.placement || "bottom",
            { forceDock: Boolean(currentStep.forceDock) },
          )
        : resolveCenteredPosition(width, height);
    const fitted = ensureTooltipInViewport(pos.top, pos.left, width, height);
    setTooltipPos({
      ...pos,
      top: fitted.top,
      left: fitted.left,
      maxHeight: fitted.maxHeight,
      docked: pos.docked,
      placed: true,
      stepKey,
    });

    const body = bodyRef.current;
    if (body) {
      const scrollable = body.scrollHeight > body.clientHeight + 1;
      setBodyScrollable(scrollable);
      setAtBodyBottom(
        body.scrollTop + body.clientHeight >= body.scrollHeight - 2,
      );
    }
  }, [rect, targetReady, currentStep, stepKey]);

  useLayoutEffect(() => {
    recomputePosition();
  }, [recomputePosition, stepIndex, isCompact, maxTooltipH]);

  useEffect(() => {
    if (
      !active ||
      !tooltipRef.current ||
      typeof ResizeObserver === "undefined"
    ) {
      return undefined;
    }
    const ro = new ResizeObserver(() => recomputePosition());
    ro.observe(tooltipRef.current);
    return () => ro.disconnect();
  }, [active, recomputePosition, stepKey]);

  const handleBodyScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    setAtBodyBottom(
      body.scrollTop + body.clientHeight >= body.scrollHeight - 2,
    );
  }, []);

  if (!active || !currentStep) return null;

  const padding = 8;
  const highlight = rect
    ? {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : null;

  const elementMeta =
    ELEMENT_TYPES[currentStep.elementType] || ELEMENT_TYPES.module;
  const progress = ((stepIndex + 1) / totalSteps) * 100;
  const awaitingPage = Boolean(
    currentStep.route && currentStep.route !== location.pathname,
  );
  const screenLabel = currentStep.route?.includes("/ropa/manual")
    ? "Assessment form"
    : currentStep.route === "/user/ropa"
      ? "Cover page"
      : currentStep.pageTitle;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="live-tour-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10050] pointer-events-none"
        aria-live="polite"
        role="dialog"
        aria-modal="true"
        aria-label={`Product tour: ${tourLabel}`}
        data-assessment-page={assessmentPage ? "true" : undefined}
      >
        {!showSpotlight && (
          <div
            className="absolute inset-0 bg-slate-900/60 pointer-events-auto"
            onClick={endTour}
            aria-hidden
          />
        )}

        {showSpotlight && (
          <>
            {/* Dimmed + clickable frame around the target — none of these
               strips cover the highlighted rect itself, so the button/card
               being explained stays fully clickable underneath. */}
            <div
              className="absolute pointer-events-auto"
              style={{
                top: 0,
                left: 0,
                right: 0,
                height: Math.max(highlight.top, 0),
                background: "rgba(15,23,42,0.68)",
              }}
              onClick={endTour}
              aria-hidden
            />
            <div
              className="absolute pointer-events-auto"
              style={{
                top: highlight.top + highlight.height,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(15,23,42,0.68)",
              }}
              onClick={endTour}
              aria-hidden
            />
            <div
              className="absolute pointer-events-auto"
              style={{
                top: highlight.top,
                left: 0,
                width: Math.max(highlight.left, 0),
                height: highlight.height,
                background: "rgba(15,23,42,0.68)",
              }}
              onClick={endTour}
              aria-hidden
            />
            <div
              className="absolute pointer-events-auto"
              style={{
                top: highlight.top,
                left: highlight.left + highlight.width,
                right: 0,
                height: highlight.height,
                background: "rgba(15,23,42,0.68)",
              }}
              onClick={endTour}
              aria-hidden
            />
          </>
        )}

        {showSpotlight && (
          <motion.div
            className="absolute pointer-events-none z-[9999]"
            style={{
              top: highlight.top,
              left: highlight.left,
              width: highlight.width,
              height: highlight.height,
              borderRadius: 12,
              boxShadow:
                "0 0 0 3px #6366f1, 0 0 0 6px rgba(99,102,241,0.25), 0 0 32px rgba(99,102,241,0.45)",
            }}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
          />
        )}

        {showSpotlight && !tooltipPos.docked && (
          <motion.div
            className="absolute pointer-events-none z-[10000]"
            style={{
              top: highlight.top + highlight.height - 6,
              left: highlight.left + highlight.width - 10,
            }}
            animate={{ y: [0, 5, 0], x: [0, 3, 0] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          >
            <MousePointer2
              size={20}
              className="text-indigo-400 drop-shadow-lg"
            />
          </motion.div>
        )}

        {/*
          Layout: a fixed-height shell (never taller than the viewport)
          split into a non-scrolling header, a scrolling body, and a
          non-scrolling footer. The Back/Next buttons live in the footer,
          so they are ALWAYS visible and clickable no matter how long the
          step's explanation is — this is what fixes cards that used to
          get cut off or trap the Next button out of view.
        */}
        <motion.div
          ref={tooltipRef}
          initial={{ opacity: 0, y: 6 }}
          animate={{
            opacity: tooltipVisible ? 1 : 0,
            y: 0,
          }}
          className={`fixed z-[10052] flex flex-col min-h-0 bg-white shadow-2xl border overflow-hidden pointer-events-auto ${
            tooltipPos.docked
              ? "border-indigo-200 ring-2 ring-indigo-100"
              : "border-slate-200/80"
          } ${isCompact ? "rounded-xl" : "rounded-2xl"}`}
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            width: `min(${maxW}px, calc(100vw - 24px))`,
            maxHeight:
              tooltipPos.maxHeight ||
              Math.min(maxTooltipH, Math.floor(window.innerHeight - 24)),
            visibility: tooltipVisible ? "visible" : "hidden",
          }}
        >
          <div
            className={`shrink-0 bg-slate-100 ${isCompact ? "h-0.5" : "h-1"}`}
          >
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-sky-400 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Sticky header */}
          <div
            className={`shrink-0 ${isCompact ? "px-3 pt-2.5" : "px-4 sm:px-5 pt-3.5"}`}
          >
            <div
              className={`flex flex-wrap items-center gap-1.5 ${isCompact ? "mb-1.5" : "mb-2"}`}
            >
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide text-white"
                style={{ background: elementMeta.color }}
              >
                {elementMeta.label}
              </span>
              {screenLabel && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-slate-100 text-slate-600">
                  <Layers size={10} />
                  {screenLabel}
                </span>
              )}
              {tooltipPos.docked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-indigo-50 text-indigo-700">
                  <Pin size={10} />
                  Pinned
                </span>
              )}
              <span className="text-[9px] text-slate-400 ml-auto tabular-nums">
                {stepIndex + 1}/{totalSteps}
              </span>
            </div>

            <div
              className={`flex items-start justify-between gap-2 ${isCompact ? "pb-2" : "pb-2.5"}`}
            >
              <h3
                className={`font-display font-bold text-slate-900 leading-snug ${
                  isCompact ? "text-sm" : "text-base"
                }`}
              >
                {currentStep.title}
              </h3>
              <button
                type="button"
                onClick={endTour}
                className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"
                aria-label="Close tour"
              >
                <X size={isCompact ? 14 : 16} />
              </button>
            </div>
          </div>

          {/* Scrollable body — only this region scrolls, so the header
              and footer (with Back/Next) never move out of reach. */}
          <div
            ref={bodyRef}
            onScroll={handleBodyScroll}
            className={`flex-1 min-h-0 overflow-y-auto ${isCompact ? "px-3" : "px-4 sm:px-5"}`}
          >
            {targetMissing && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 mb-2">
                We couldn't find this on screen — it may be further down the
                page, or hidden behind a menu. Scroll around, or just tap Next
                to continue.
              </p>
            )}

            {awaitingPage ? (
              <p
                className={`text-slate-600 leading-relaxed ${
                  isCompact ? "text-xs mb-2" : "text-sm mb-3"
                }`}
              >
                Opening the ROPA assessment form. This step explains controls
                on that screen — not the cover page.
              </p>
            ) : (
              <p
                className={`text-slate-600 leading-relaxed ${
                  isCompact ? "text-xs mb-2" : "text-sm mb-3"
                }`}
              >
                {currentStep.body}
              </p>
            )}

            {!awaitingPage && !isCompact && currentStep.action && (
              <div className="space-y-2 mb-3">
                <div className="rounded-lg bg-indigo-50/80 border border-indigo-100 p-2.5">
                  <p className="text-[10px] font-semibold text-indigo-700 uppercase mb-0.5">
                    What to do
                  </p>
                  <p className="text-xs text-slate-700">{currentStep.action}</p>
                </div>
                {currentStep.outcome && (
                  <div className="rounded-lg bg-emerald-50/80 border border-emerald-100 p-2.5 flex gap-2">
                    <CheckCircle2
                      size={14}
                      className="text-emerald-600 shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="text-[10px] font-semibold text-emerald-700 uppercase mb-0.5">
                        Expected outcome
                      </p>
                      <p className="text-xs text-slate-700">
                        {currentStep.outcome}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!awaitingPage && isCompact && currentStep.action && (
              <p className="text-[11px] text-indigo-700 bg-indigo-50/70 rounded-lg px-2 py-1.5 mb-2">
                → {currentStep.action}
              </p>
            )}

            {!awaitingPage && currentStep.tip && (
              <p
                className={`text-slate-600 flex gap-1.5 ${isCompact ? "text-[11px] mb-1.5" : "text-xs mb-2"}`}
              >
                <Lightbulb
                  size={12}
                  className="text-amber-500 shrink-0 mt-0.5"
                />
                {currentStep.tip}
              </p>
            )}
            {!awaitingPage && currentStep.warning && (
              <p className="text-[11px] text-red-700 bg-red-50 border border-red-100 rounded-lg p-2 mb-2 flex gap-1.5">
                <AlertTriangle size={12} className="shrink-0" />
                {currentStep.warning}
              </p>
            )}
            <div className="h-1" />
          </div>

          {/* Subtle "more below" affordance instead of leaving the user
              guessing why the card looks cut off. */}
          {bodyScrollable && !atBodyBottom && (
            <div className="shrink-0 flex justify-center -mt-1 pointer-events-none">
              <span className="inline-flex items-center gap-0.5 text-[9px] text-slate-400 bg-white/95 px-1.5 rounded-full">
                <ChevronDown size={10} />
                more
              </span>
            </div>
          )}

          {/* Sticky footer — always visible inside the card */}
          <div
            className={`shrink-0 flex items-center justify-between gap-2 border-t border-slate-100 bg-white ${
              isCompact ? "px-3 py-2" : "px-4 sm:px-5 py-2.5"
            }`}
          >
            <button
              type="button"
              onClick={prevStep}
              disabled={stepIndex === 0 || waitingForTarget}
              className={`inline-flex items-center gap-1 rounded-lg font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none ${
                isCompact ? "px-2 py-1.5 text-[11px]" : "px-3 py-2 text-xs"
              }`}
            >
              <ChevronLeft size={13} />
              Back
            </button>
            <button
              type="button"
              onClick={() => nextStep()}
              disabled={waitingForTarget}
              className={`inline-flex items-center gap-1 rounded-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:pointer-events-none ${
                isCompact ? "px-3 py-1.5 text-[11px]" : "px-4 py-2 text-xs"
              }`}
            >
              {stepIndex >= totalSteps - 1 ? "Finish" : "Next"}
              <ChevronRight size={13} />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
